import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Monitor } from 'lucide-react';

type ConfirmAlertDialogProps = {
  open: boolean;
  title: string;
  description?: string;
  /** Optional bullet list (e.g. displays still missing hours) */
  items?: string[];
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'warning' | 'default';
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * In-app alert / confirm dialog — replaces browser alerts and long toasts
 * for actionable schedule validation.
 */
export function ConfirmAlertDialog({
  open,
  title,
  description,
  items,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger',
  onCancel,
  onConfirm,
}: ConfirmAlertDialogProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open || typeof document === 'undefined') return null;

  const confirmClass =
    tone === 'danger'
      ? 'bg-rose-600 text-white hover:bg-rose-700'
      : 'bg-[var(--dash-brand)] text-white hover:brightness-105';

  const iconWrap =
    tone === 'danger'
      ? 'bg-rose-50 text-rose-600'
      : tone === 'warning'
        ? 'bg-amber-50 text-amber-700'
        : 'bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]';

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
        aria-label="Dismiss"
        onClick={onCancel}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-alert-title"
        aria-describedby="confirm-alert-desc"
        className="camp-fade-up relative z-10 w-full max-w-[420px] overflow-hidden rounded-2xl border border-[#E6E8EF] bg-white shadow-2xl"
      >
        <div className="flex gap-3 px-5 pt-5 pb-1">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconWrap}`}
          >
            <AlertTriangle className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0 text-left">
            <h2
              id="confirm-alert-title"
              className="m-0 text-[16px] font-semibold tracking-tight text-[#1F2433]"
            >
              {title}
            </h2>
            {description ? (
              <p
                id="confirm-alert-desc"
                className="mt-1.5 mb-0 text-[13px] leading-relaxed text-[#6B7289]"
              >
                {description}
              </p>
            ) : (
              <span id="confirm-alert-desc" className="sr-only">
                {title}
              </span>
            )}
          </div>
        </div>

        {items && items.length > 0 ? (
          <ul className="mx-5 mt-3 mb-0 max-h-[40vh] list-none space-y-1.5 overflow-y-auto rounded-xl border border-[#EEF0F5] bg-[#F8F9FB] p-2">
            {items.map((item) => (
              <li
                key={item}
                className="flex items-start gap-2 rounded-lg bg-white px-2.5 py-2 text-[12.5px] font-medium text-[#1F2433]"
              >
                <Monitor
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--dash-brand)]"
                  aria-hidden
                />
                <span className="min-w-0 break-words">{item}</span>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="mt-4 flex flex-col-reverse gap-2 border-t border-[#EEF0F5] bg-[#F8F9FB] px-5 py-3.5 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-10 items-center justify-center rounded-xl border border-[#E6E8EF] bg-white px-4 text-[13px] font-semibold text-[#1F2433] transition hover:bg-[#F3F4F8]"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`inline-flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-semibold transition ${confirmClass}`}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
